import { filter } from 'rxjs/operators';
import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { APIResponse } from "src/app/core/models/api-response";
import { environment } from "src/environments/environment";
import { IPageCount } from "src/app/shared/models/page.model";
import { AddNewLocationPayload, ContactCreatePayload, ContactUpdatePayload, CreateLocationPayload, LocationUpdatePayload, ICompanyFilterRequest, ICountry, ICurrency, ICustomer, ILocation, IPayment_term, IState, LocationContact } from "../models/customers.models";

@Injectable({
  providedIn: 'root',
})
export class CustomersService {

  constructor(private http: HttpClient) { }

  public listAllCustomers(): Observable<APIResponse<ICustomer>> {
    return this.http.get<APIResponse<ICustomer>>(environment.BASE_URL_API + 'companies/list?limit=500&sort_by=name&sort_mode=asc');
  }

  public listAllLocations(): Observable<APIResponse<ILocation>> {
    return this.http.get<APIResponse<ILocation>>(environment.BASE_URL_API + 'locations');
  }

  /** `filter` is JSON-stringified for the backend `filters` param. */
  public listLocations(
    page: IPageCount,
    filter?: ICompanyFilterRequest | undefined,
    search?: string | undefined,
    sort_by: string = 'id',
    sort_mode: string = 'desc',
  ): Observable<APIResponse<ILocation>> {
    const params = new HttpParams()
      .set('page', page.number)
      .set('limit', page.size)
      .set('filters', JSON.stringify(filter))
      .set('key', search!)
      .set('sort_by', sort_by)
      .set('sort_mode', sort_mode);
    return this.http.get<APIResponse<ILocation>>(
      environment.BASE_URL_API + 'locations',
      { params }
    )
  }

  public listCustomers(
    page: IPageCount,
    filter?: ICompanyFilterRequest | undefined,
    search?: string | undefined,
    sort_by: string = 'id',
    sort_mode: string = 'desc',
    subsidiary_id?: number | undefined,
    deleted: 'false' | 'true' | 'all' = 'false',
  ): Observable<APIResponse<ICustomer>> {
    const params = new HttpParams()
      .set('page', page.number)
      .set('limit', page.size)
      .set('filters', JSON.stringify(filter))
      .set('key', search!)
      .set('sort_by', sort_by)
      .set('sort_mode', sort_mode)
      .set('subsidiary_id', subsidiary_id! ?? undefined)
      .set('deleted', deleted);
    return this.http.get<APIResponse<ICustomer>>(
      environment.BASE_URL_API + 'companies',
      { params }
    )
  }

  /** Despite the name, loads `/locations/:id` with full company detail. */
  public getCustomerById(id: number): Observable<APIResponse<ICustomer>> {
    return this.http.get<APIResponse<ICustomer>>(environment.BASE_URL_API + 'locations/' + id);
  }

  public getCompanyById(id: number): Observable<APIResponse<ICustomer>> {
    return this.http.get<APIResponse<ICustomer>>(environment.BASE_URL_API + 'companies/' + id);
  }

  public addCustomer(customer: Omit<ICustomer, 'id'>): Observable<APIResponse<ICustomer>> {
    return this.http.post<APIResponse<ICustomer>>(
      environment.BASE_URL_API + 'companies',
      customer
    );
  }

  /** `POST /companies/:id/locations`. */
  public addLocation(id: number, customer: Omit<ILocation, 'id'>): Observable<APIResponse<ILocation>> {
    return this.http.post<APIResponse<ILocation>>(
      environment.BASE_URL_API + 'companies/' + id + '/locations',
      customer
    );
  }

  /** Standalone `POST /locations` with minimal payload. */
  public createLocation(payload: CreateLocationPayload): Observable<APIResponse<ILocation>> {
    return this.http.post<APIResponse<ILocation>>(
      environment.BASE_URL_API + 'locations',
      payload
    );
  }

  /**
   * `POST /locations/create` — create a shipping location inline during the order flow.
   * Response is flow-dependent (see {@link AddNewLocationPayload}); typed loosely so the
   * caller can branch on whether `order_id` was supplied.
   */
  public addNewLocation(payload: AddNewLocationPayload): Observable<APIResponse<any>> {
    return this.http.post<APIResponse<any>>(
      environment.BASE_URL_API + 'locations/create',
      payload
    );
  }

  /** PATCH partial update; send `null` to clear optional fields. */
  public updateLocation(locationId: number, payload: LocationUpdatePayload): Observable<APIResponse<ILocation>> {
    return this.http.patch<APIResponse<ILocation>>(
      `${environment.BASE_URL_API}locations/${locationId}`,
      payload
    );
  }

  public updateCustomer(id: number, customer: Omit<ICustomer, 'id'>): Observable<APIResponse<ICustomer>> {
    return this.http.patch<APIResponse<ICustomer>>(
      environment.BASE_URL_API + 'companies/' + id,
      customer
    );
  }

  public CustomerApprobation(id: number, status: string): Observable<APIResponse<ICustomer>> {
    return this.http.post<APIResponse<ICustomer>>(
      `${environment.BASE_URL_API}companies/${id}/approbation`,{ status }
    );
  }

  public deleteCustomer(id: number): Observable<APIResponse<ICustomer>> {
    return this.http.delete<APIResponse<ICustomer>>(
      environment.BASE_URL_API + 'companies/' + id,
    );
  }

  /** `POST /companies/:id/restore` — reactivate a soft-deleted company. */
  public restoreCustomer(id: number): Observable<APIResponse<ICustomer>> {
    return this.http.post<APIResponse<ICustomer>>(
      environment.BASE_URL_API + 'companies/' + id + '/restore',
      {}
    );
  }

  public deleteLocation(id: number): Observable<APIResponse<ICustomer>> {
    return this.http.delete<APIResponse<ICustomer>>(
      environment.BASE_URL_API + 'locations/' + id,
    );
  }

  public listAllPaymentTerms(): Observable<APIResponse<IPayment_term>> {
    return this.http.get<APIResponse<IPayment_term>>(environment.BASE_URL_API + 'payment_terms');
  }

  /**
   * @param name Free-text filter (ilike on the country name).
   * @param code ISO 3166-1 alpha-2 exact match -- locale-independent, unlike
   * `name` (WB-7137 country auto-select from a Google Places result).
   */
  public listAllCountries(name?: string, code?: string): Observable<APIResponse<ICountry>> {
    let params = new HttpParams();
    if (name) params = params.set('name', name);
    if (code) params = params.set('code', code);
    return this.http.get<APIResponse<ICountry>>(environment.BASE_URL_API + 'resources/countries', { params });
  }

  /**
   * @param countryId Scopes the list to one country (WB-7137: the Province/State
   * dropdown only populates once a country is known).
   */
  public listAllStates(name?: string, countryId?: number): Observable<APIResponse<IState>> {
    let params = new HttpParams();
    if (name) params = params.set('name', name);
    if (countryId) params = params.set('country_id', countryId);
    return this.http.get<APIResponse<IState>>(environment.BASE_URL_API + 'resources/states', { params });
  }

  public listAllCurrencies(): Observable<APIResponse<ICurrency>> {
    return this.http.get<APIResponse<ICurrency>>(environment.BASE_URL_API + 'resources/currencies');
  }

  public exportCompanies(
    filter: ICompanyFilterRequest | undefined,
    search: string | undefined
  ) {
    const params = new HttpParams().set('filters', JSON.stringify(filter)).set('key', search!);
    return this.http.get(environment.BASE_URL_API + 'companies/export', {
      responseType: 'blob', params
    });
  }

  public resendInvitation(contactId: number): Observable<APIResponse<any>> {
    return this.http.post<APIResponse<any>>(
      `${environment.BASE_URL_API}users/invite/${contactId}`,
      {}
    );
  }

  public createLocationContact(
    locationId: number,
    payload: ContactCreatePayload
  ): Observable<APIResponse<LocationContact>> {
    return this.http.post<APIResponse<LocationContact>>(
      `${environment.BASE_URL_API}locations/${locationId}/contacts`,
      payload
    );
  }

  public updateLocationContact(
    locationId: number,
    contactId: number,
    payload: ContactUpdatePayload
  ): Observable<APIResponse<LocationContact>> {
    return this.http.patch<APIResponse<LocationContact>>(
      `${environment.BASE_URL_API}locations/${locationId}/contacts/${contactId}`,
      payload
    );
  }

  public deleteLocationContact(locationId: number, contactId: number): Observable<void> {
    return this.http.delete<void>(
      `${environment.BASE_URL_API}locations/${locationId}/contacts/${contactId}`
    );
  }
}
